/**
 * <collection-search-component>
 *
 * Self-contained, isolated behavior for the "search this collection" input.
 * Does not read or write any state belonging to the native facets/filters
 * system, and does not touch the results-list/infinite-scroll component, so
 * it cannot interfere with either.
 *
 * This snippet is rendered TWICE per page (once in the horizontal desktop
 * filter bar, once in the mobile drawer), so two instances of this
 * element always exist in the DOM at once, even though only one is visible
 * at a given viewport size. The active query, the empty-state element, and
 * the infinite-scroll observer are therefore shared at the CLASS level
 * (static), not per-instance: typing in one instance's box is reflected in
 * the other's, and only one component ever actually filters the (single,
 * shared) product grid or owns the "not found" message, regardless of how
 * many instances exist.
 *
 * - Live-filters product cards already rendered on the page by substring
 *   matching against each card's visible text (title, vendor if shown, etc).
 * - When nothing on the page matches, shows a clear "not found" message and
 *   suggests the most relevant collection to explore instead, using
 *   Shopify's own documented predictive search endpoint
 *   (/search/suggest.json) to find the best-matching product store-wide,
 *   then mapping that product's type/title to one of this store's real
 *   collections. Falls back to a full-catalog search link (never to
 *   /collections/all) when no confident match is found.
 * - Submitting the form (Enter key) navigates to the theme's native
 *   site-wide search results endpoint, so shoppers can still find matches
 *   beyond what happens to be loaded on the current page.
 * - Re-applies automatically whenever infinite scroll adds more products
 *   to the page, so the "not found" message can't go stale.
 */

// Title/description keyword -> collection map. These mirror the
// TITLE-CONTAINS smart-collection rules used when the collections were
// created, so a keyword match here is a precise, high-confidence signal.
const KEYWORD_COLLECTIONS = [
  { keywords: ['curling iron', 'curling wand'], handle: 'curling-irons', title: 'Curling Irons' },
  { keywords: ['flat iron', 'straightener'], handle: 'flat-iron', title: 'Flat Iron' },
  { keywords: ['sprayer', 'spray bottle'], handle: 'sprayers', title: 'Sprayers' },
  { keywords: ['disinfectant'], handle: 'disinfectants', title: 'Disinfectants' },
  { keywords: ['neck duster'], handle: 'neck-dusters', title: 'Neck Dusters' },
  { keywords: ['barber pole'], handle: 'barber-poles', title: 'Barber Poles' },
  { keywords: ['disposable'], handle: 'disposable-supplies', title: 'Disposable Supplies' },
  { keywords: ['hair dryer', 'blow dryer'], handle: 'hair-dryers', title: 'Hair Dryers' },
  { keywords: ['neck strip'], handle: 'neck-strips', title: 'Neck Strips' },
  { keywords: ['ring light'], handle: 'ring-lights', title: 'Ring Lights' },
  { keywords: ['hair spray', 'hairspray'], handle: 'hair-spray', title: 'Hair Spray' },
  { keywords: ['barbicide'], handle: 'barbicide', title: 'Barbicide' },
  { keywords: ['developer'], handle: 'developers', title: 'Developers' },
  { keywords: ['shave gel'], handle: 'shave-gel', title: 'Shave Gel' },
  { keywords: ['leave-in', 'leave in'], handle: 'leave-in-products', title: 'Leave-In Products' },
  { keywords: ['texture'], handle: 'texture-products', title: 'Texture Products' },
  { keywords: ['sanitiz'], handle: 'sanitizer', title: 'Sanitizer' },
  { keywords: ['trimmer'], handle: 'trimmers', title: 'Trimmers' },
  { keywords: ['clipper'], handle: 'clippers', title: 'Clippers' },
  { keywords: ['pomade'], handle: 'pomades', title: 'Pomades' },
  { keywords: ['bleach'], handle: 'bleach', title: 'Bleach' },
  { keywords: ['shaver'], handle: 'shavers', title: 'Shavers' },
  { keywords: ['paste'], handle: 'pastes', title: 'Pastes' },
  { keywords: ['cream'], handle: 'creams', title: 'Creams' },
  { keywords: ['toner'], handle: 'toners', title: 'Toners' },
  { keywords: ['shear'], handle: 'shears', title: 'Shears' },
  { keywords: ['brush'], handle: 'combs-brushes', title: 'Combs & Brushes' },
  { keywords: ['razor'], handle: 'razors-blades', title: 'Razors & Blades' },
  { keywords: ['glove'], handle: 'gloves', title: 'Gloves' },
  { keywords: ['towel'], handle: 'towels', title: 'Towels' },
  { keywords: ['clay'], handle: 'clays', title: 'Clays' },
  { keywords: ['foil'], handle: 'color-accessories', title: 'Color Accessories' },
  { keywords: ['comb'], handle: 'combs-brushes', title: 'Combs & Brushes' },
  { keywords: ['cape'], handle: 'capes', title: 'Barber Capes' },
  { keywords: ['talc'], handle: 'talc-powders', title: 'Talc Powders' },
  { keywords: ['gel'], handle: 'gels', title: 'Gels' },
  { keywords: ['oil'], handle: 'oils', title: 'Oils' },
];

// Shopify product_type -> collection map, built from this store's actual
// catalog product types and the collections that already cover them.
const PRODUCT_TYPE_COLLECTIONS = {
  'aftershave/cologne': { handle: 'aftershave-cologne', title: 'Aftershave/Cologne' },
  'anti-fatigue mats': { handle: 'anti-fatigue-mats', title: 'Anti-Fatigue Mats' },
  brush: { handle: 'combs-brushes', title: 'Combs & Brushes' },
  brushes: { handle: 'combs-brushes', title: 'Combs & Brushes' },
  'barber poles': { handle: 'barber-poles', title: 'Barber Poles' },
  blades: { handle: 'blades', title: 'Blades' },
  capes: { handle: 'capes', title: 'Barber Capes' },
  'clipper & trimmer accessories': { handle: 'barber-accessories', title: 'Barber Accessories' },
  'clipper & trimmer attachments': { handle: 'guards', title: 'Guards' },
  'clipper and shaver/trimmer combos': { handle: 'clipper-and-shaver-trimmer-combos', title: 'Clipper and Shaver/Trimmer Combos' },
  clippers: { handle: 'clippers', title: 'Clippers' },
  combs: { handle: 'combs-brushes', title: 'Combs & Brushes' },
  'curling irons': { handle: 'curling-irons', title: 'Curling Irons' },
  disinfectants: { handle: 'disinfectants', title: 'Disinfectants' },
  'facial products': { handle: 'facial-products', title: 'Facial Products' },
  'flat iron/curling iron': { handle: 'styling-tools', title: 'Styling Tools' },
  'flat iron/dryer': { handle: 'styling-tools', title: 'Styling Tools' },
  'flat iron': { handle: 'flat-iron', title: 'Flat Iron' },
  'gift card': { handle: 'gift-card', title: 'Gift Card' },
  gloves: { handle: 'gloves', title: 'Gloves' },
  'hair dryers': { handle: 'hair-dryers', title: 'Hair Dryers' },
  'hair grips': { handle: 'hair-grips', title: 'Hair Grips' },
  'hair growth supplements': { handle: 'oral-hair-growth-supplements', title: 'Hair Growth Supplements' },
  'hair spray': { handle: 'hair-spray', title: 'Hair Spray' },
  'hair treatments': { handle: 'hair-treatments', title: 'Hair Treatments' },
  'hair and body wash': { handle: 'hair-and-body-wash', title: 'Hair and Body Wash' },
  massagers: { handle: 'massagers', title: 'Massagers' },
  'mobile solutions': { handle: 'salon-accessories', title: 'Salon Accessories' },
  'neck dusters': { handle: 'neck-dusters', title: 'Neck Dusters' },
  'neck strips': { handle: 'neck-strips', title: 'Neck Strips' },
  organization: { handle: 'salon-accessories', title: 'Salon Accessories' },
  'permanent hair color': { handle: 'hair-color', title: 'Hair Color' },
  'power packs': { handle: 'power-packs', title: 'Power Packs' },
  'professional wear': { handle: 'professional-wear', title: 'Professional Wear' },
  'razors & blades': { handle: 'razors-blades', title: 'Razors & Blades' },
  'ring lights': { handle: 'ring-lights', title: 'Ring Lights' },
  'semi permanent hair color': { handle: 'hair-color', title: 'Hair Color' },
  'semi perm color/treatment': { handle: 'hair-color', title: 'Hair Color' },
  'shampoo/condition/beard products': { handle: 'shampoo-condition-beard-products', title: 'Shampoo/Condition/Beard Products' },
  'shampoo/conditioner/treatment': { handle: 'shampoo-conditioner-treatment', title: 'Shampoo/Conditioner/Treatment' },
  'shave gel': { handle: 'shave-gel', title: 'Shave Gel' },
  shavers: { handle: 'shavers', title: 'Shavers' },
  shears: { handle: 'shears', title: 'Shears' },
  'skin care': { handle: 'skin-care', title: 'Skin Care' },
  'specialty irons': { handle: 'styling-tools', title: 'Styling Tools' },
  sprayers: { handle: 'sprayers', title: 'Sprayers' },
  straighteners: { handle: 'styling-tools', title: 'Styling Tools' },
  'styling power packs': { handle: 'styling-power-packs', title: 'Styling Power Packs' },
  'styling products': { handle: 'styling-products', title: 'Styling Products' },
  'styling tool accessories': { handle: 'styling-tools', title: 'Styling Tools' },
  'talc powders': { handle: 'talc-powders', title: 'Talc Powders' },
  towels: { handle: 'towels', title: 'Towels' },
  'trimmer/shaver': { handle: 'trimmer-shaver', title: 'Trimmer/Shaver' },
  trimmers: { handle: 'trimmers', title: 'Trimmers' },
  'wax & wax warmers': { handle: 'wax-wax-warmers', title: 'Wax & Wax Warmers' },
  'wax prep and aftercare': { handle: 'wax-prep-and-aftercare', title: 'Wax Prep and Aftercare' },
  'wax prep & aftercare': { handle: 'wax-prep-aftercare', title: 'Wax Prep & Aftercare' },
  'wax strips and accessories': { handle: 'wax-strips-and-accessories', title: 'Wax Strips and Accessories' },
  'wax strips & accessories': { handle: 'wax-strips-accessories', title: 'Wax Strips & Accessories' },
};

/**
 * Finds the best collection suggestion for a search query, first by
 * keyword (precise, no network needed), then by looking up the top
 * matching product store-wide via Shopify's predictive search endpoint
 * and mapping its product_type to a collection.
 *
 * @param {string} query
 * @param {AbortSignal} signal
 * @returns {Promise<{title: string, handle: string} | null>}
 */
async function findCollectionSuggestion(query, signal) {
  const lowerQuery = query.toLowerCase();

  for (const entry of KEYWORD_COLLECTIONS) {
    if (entry.keywords.some((keyword) => lowerQuery.includes(keyword))) {
      return { title: entry.title, handle: entry.handle };
    }
  }

  try {
    const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=3&resources[options][unavailable_products]=hide&resources[options][fields]=title,product_type`;
    const response = await fetch(url, { signal });
    if (!response.ok) return null;

    const data = await response.json();
    const products = data?.resources?.results?.products || [];

    for (const product of products) {
      const productTitle = (product.title || '').toLowerCase();

      for (const entry of KEYWORD_COLLECTIONS) {
        if (entry.keywords.some((keyword) => productTitle.includes(keyword))) {
          return { title: entry.title, handle: entry.handle };
        }
      }

      const productType = (product.product_type || product.type || '').toLowerCase().trim();
      if (productType && PRODUCT_TYPE_COLLECTIONS[productType]) {
        return PRODUCT_TYPE_COLLECTIONS[productType];
      }
    }
  } catch (error) {
    if (error.name !== 'AbortError') {
      // Fail silently — the "not found" message still shows without a
      // suggestion, which is a safe, honest fallback.
    }
  }

  return null;
}

class CollectionSearchComponent extends HTMLElement {
  static selector = 'collection-search-component';

  // --- Shared (class-level) state across all rendered instances ---
  static #currentQuery = '';
  static #suggestionController = null;
  static #emptyStateEl = null;
  static #gridObserver = null;
  static #observerDebounce = null;
  static #instances = new Set();

  #input = null;
  #status = null;
  #debounceTimer = null;
  #boundOnInput = null;
  #boundOnSubmit = null;

  connectedCallback() {
    this.#input = this.querySelector('[ref="input"]');
    this.#status = this.querySelector('[ref="status"]');

    if (!this.#input) return;

    // If another instance already has an active query (e.g. the desktop
    // bar was typed into before the mobile drawer was ever opened), reflect
    // it here immediately so both stay visually in sync.
    if (CollectionSearchComponent.#currentQuery) {
      this.#input.value = CollectionSearchComponent.#currentQuery;
    }

    this.#boundOnInput = () => {
      window.clearTimeout(this.#debounceTimer);
      this.#debounceTimer = window.setTimeout(() => this.#handleInput(), 150);
    };
    this.#boundOnSubmit = (event) => this.#handleSubmit(event);

    this.#input.addEventListener('input', this.#boundOnInput);
    this.addEventListener('submit', this.#boundOnSubmit);

    CollectionSearchComponent.#instances.add(this);

    // Only the first live instance needs to set up the shared observer; it
    // drives every instance's re-filter via the static filter method.
    if (!CollectionSearchComponent.#gridObserver) {
      const resultsList = document.querySelector('results-list');
      if (resultsList) {
        CollectionSearchComponent.#gridObserver = new MutationObserver(() => {
          window.clearTimeout(CollectionSearchComponent.#observerDebounce);
          CollectionSearchComponent.#observerDebounce = window.setTimeout(
            () => CollectionSearchComponent.#filter(),
            50
          );
        });
        CollectionSearchComponent.#gridObserver.observe(resultsList, { childList: true, subtree: true });
      }
    }
  }

  disconnectedCallback() {
    window.clearTimeout(this.#debounceTimer);
    if (this.#input && this.#boundOnInput) {
      this.#input.removeEventListener('input', this.#boundOnInput);
    }
    if (this.#boundOnSubmit) {
      this.removeEventListener('submit', this.#boundOnSubmit);
    }
    CollectionSearchComponent.#instances.delete(this);

    if (CollectionSearchComponent.#instances.size === 0) {
      CollectionSearchComponent.#gridObserver?.disconnect();
      CollectionSearchComponent.#gridObserver = null;
      window.clearTimeout(CollectionSearchComponent.#observerDebounce);
      CollectionSearchComponent.#suggestionController?.abort();
      CollectionSearchComponent.#emptyStateEl?.remove();
      CollectionSearchComponent.#emptyStateEl = null;
    }
  }

  #handleSubmit(event) {
    const value = this.#input.value.trim();
    if (!value) {
      event.preventDefault();
    }
  }

  #handleInput() {
    const value = this.#input.value;
    CollectionSearchComponent.#currentQuery = value;

    // Mirror the typed value into every other instance so opening the
    // other surface (e.g. the mobile drawer) shows the same query.
    CollectionSearchComponent.#instances.forEach((instance) => {
      if (instance !== this && instance.#input && instance.#input.value !== value) {
        instance.#input.value = value;
      }
    });

    CollectionSearchComponent.#filter();
  }

  static #getGridParent() {
    const anyItem = document.querySelector('.product-grid__item');
    return anyItem ? anyItem.parentElement : null;
  }

  /**
   * Finds the single shared empty-state element by its class name rather
   * than trusting a cached reference, so a stale/duplicate reference can
   * never cause a second element to be created.
   */
  static #getEmptyStateEl() {
    const existing = document.querySelector('.collection-search-empty');
    if (existing) {
      CollectionSearchComponent.#emptyStateEl = existing;
      return existing;
    }

    const gridParent = CollectionSearchComponent.#getGridParent();
    if (!gridParent) return null;

    const el = document.createElement('div');
    el.className = 'collection-search-empty';
    el.hidden = true;
    el.innerHTML = `
      <p class="collection-search-empty__message"></p>
      <a class="collection-search-empty__cta" href="#" hidden></a>
    `;
    gridParent.insertAdjacentElement('beforebegin', el);
    CollectionSearchComponent.#emptyStateEl = el;
    return el;
  }

  static #showEmptyState(query) {
    const emptyStateEl = CollectionSearchComponent.#getEmptyStateEl();
    if (!emptyStateEl) return;

    const message = emptyStateEl.querySelector('.collection-search-empty__message');
    const cta = /** @type {HTMLAnchorElement} */ (emptyStateEl.querySelector('.collection-search-empty__cta'));

    if (message) {
      message.textContent = `Not found in this collection`;
    }
    if (cta) {
      cta.hidden = true;
    }

    emptyStateEl.hidden = false;

    CollectionSearchComponent.#suggestionController?.abort();
    CollectionSearchComponent.#suggestionController = new AbortController();
    const { signal } = CollectionSearchComponent.#suggestionController;

    findCollectionSuggestion(query, signal).then((suggestion) => {
      if (signal.aborted || !cta) return;

      if (suggestion) {
        cta.textContent = `Explore ${suggestion.title}`;
        cta.href = `/collections/${suggestion.handle}`;
      } else {
        cta.textContent = `Search our full catalog for "${query}"`;
        cta.href = `/search?q=${encodeURIComponent(query)}&type=product`;
      }
      cta.hidden = false;
    });
  }

  static #hideEmptyState() {
    if (CollectionSearchComponent.#emptyStateEl) {
      CollectionSearchComponent.#emptyStateEl.hidden = true;
    }
    CollectionSearchComponent.#suggestionController?.abort();
  }

  static #filter() {
    const query = CollectionSearchComponent.#currentQuery.trim().toLowerCase();
    const items = document.querySelectorAll('.product-grid__item');
    let visibleCount = 0;

    items.forEach((item) => {
      if (!query) {
        item.removeAttribute('data-search-hidden');
      } else {
        const text = item.textContent.toLowerCase();
        if (text.includes(query)) {
          item.removeAttribute('data-search-hidden');
        } else {
          item.setAttribute('data-search-hidden', 'true');
        }
      }

      // A product can also be hidden by the brand filter running
      // independently on the same grid. Without checking that here too,
      // a search match that happens to belong to a brand the shopper has
      // filtered out would still count as "visible" — showing a
      // misleading result count and suppressing the "not found" message
      // even though nothing is actually on screen.
      const isHiddenBySearch = item.getAttribute('data-search-hidden') === 'true';
      const isHiddenByBrand = item.getAttribute('data-brand-hidden') === 'true';
      if (!isHiddenBySearch && !isHiddenByBrand) {
        visibleCount += 1;
      }
    });

    if (query && visibleCount === 0) {
      CollectionSearchComponent.#showEmptyState(CollectionSearchComponent.#currentQuery.trim());
    } else {
      CollectionSearchComponent.#hideEmptyState();
    }

    const statusText = query
      ? `${visibleCount} result${visibleCount === 1 ? '' : 's'} found on this page for "${query}". Press enter to search the full catalog.`
      : '';
    CollectionSearchComponent.#instances.forEach((instance) => {
      if (instance.#status) instance.#status.textContent = statusText;
    });
  }
}

if (!customElements.get(CollectionSearchComponent.selector)) {
  customElements.define(CollectionSearchComponent.selector, CollectionSearchComponent);
}
